/**
 * StudioPublicShare — read-only public client view (no login).
 *
 * Resolves the slug at /r/{slug} → /api/studio/public/{slug} and shows
 * a polished, brand-aligned snapshot of the channel for the client.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Youtube, Calendar, TrendingUp, CheckCircle2, Loader2, AlertTriangle,
  Users, Sparkles, BarChart3, Mail, Inbox, X, Check, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STAGE_LABELS = {
  idea: "Idea", scripting: "Scripting", script_review: "Script Review",
  recording: "Recording", editing: "Editing", edit_review: "Edit Review",
  thumbnail: "Thumbnail", thumbnail_review: "Thumb Review",
  seo: "SEO", scheduling: "Scheduling", scheduled: "Scheduled",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  } catch (e) { return iso; }
}
function fmtNum(n) {
  if (n == null) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
}

export default function StudioPublicShare() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const verifyToken = params.get("token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [signedInAs, setSignedInAs] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [decidingId, setDecidingId] = useState(null);
  const [noteFor, setNoteFor] = useState({});
  const verifiedRef = useRef(null);  // guard StrictMode double-fire

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/api/studio/public/${slug}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((err) => { if (alive) setError(err?.response?.data?.detail || err.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // Verify magic-link token if present in URL
  useEffect(() => {
    if (!verifyToken) return;
    if (verifiedRef.current === verifyToken) return;  // already verified this token (StrictMode)
    verifiedRef.current = verifyToken;
    axios
      .get(`${API}/api/studio/public/${slug}/verify?token=${verifyToken}`, { withCredentials: true })
      .then((r) => {
        setSignedInAs(r.data.email);
        toast.success("Signed in to client portal");
        loadInbox();
        // clean token from URL
        window.history.replaceState({}, "", `/r/${slug}`);
      })
      .catch((err) => {
        // Silent on duplicate-use 400 if we already signed in via the first call.
        if (signedInAs) return;
        toast.error(err?.response?.data?.detail || "Link invalid");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyToken, slug]);

  const loadInbox = async () => {
    setInboxLoading(true);
    try {
      const r = await axios.get(`${API}/api/studio/public/${slug}/inbox`, { withCredentials: true });
      setInbox(r.data);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error(err?.response?.data?.detail || "Could not load inbox");
      }
      setInbox(null);
    } finally {
      setInboxLoading(false);
    }
  };

  const requestLink = async () => {
    if (!emailInput.includes("@")) { toast.error("Valid email please"); return; }
    setLinkBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/studio/public/${slug}/request-link`,
        { email: emailInput.trim().toLowerCase() }
      );
      setLinkSent(true);
      toast.success(r.data.email_sent ? "Check your email for the access link" : "Link created (email send may be disabled)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send link");
    } finally {
      setLinkBusy(false);
    }
  };

  const decide = async (content_id, decision) => {
    setDecidingId(content_id);
    try {
      await axios.post(
        `${API}/api/studio/public/${slug}/decide`,
        { content_id, decision, note: noteFor[content_id] || "" },
        { withCredentials: true }
      );
      toast.success(decision === "approve" ? "Approved" : "Changes requested");
      setNoteFor((s) => { const n = { ...s }; delete n[content_id]; return n; });
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save decision");
    } finally {
      setDecidingId(null);
    }
  };

  if (loading) {
    return (
      <div className="studio-shell" style={{ paddingTop: 80, textAlign: "center" }}>
        <Loader2 size={28} className="ac-spin" />
        <p style={{ color: "var(--st-text-muted)", marginTop: 12 }}>Loading client view…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="studio-shell" style={{ paddingTop: 80 }}>
        <div className="ac-error" data-testid="public-error">
          <AlertTriangle size={16} /> {error}
        </div>
        <p style={{ color: "var(--st-text-muted)", textAlign: "center", marginTop: 32 }}>
          This share link is either invalid or has been disabled by the channel owner.
        </p>
      </div>
    );
  }
  if (!data) return null;

  const { channel, pipeline, pipeline_total, recent_publishes, upcoming, pending_approvals, top_videos } = data;

  return (
    <div className="studio-shell" data-testid="public-shell">
      {/* Hero */}
      <div className="studio-hero" data-testid="public-hero">
        <span className="studio-eyebrow">
          <Sparkles size={12} /> Client portal · powered by MarkeeMix Studio
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
          {channel.thumbnail_url ? (
            <img src={channel.thumbnail_url} alt="" style={{ width: 60, height: 60, borderRadius: 12 }} />
          ) : (
            <div style={{ width: 60, height: 60, borderRadius: 12, background: "var(--st-card)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Youtube size={28} />
            </div>
          )}
          <div>
            <h1 className="studio-h1" style={{ margin: 0, fontSize: "2.2rem" }} data-testid="public-channel-title">
              {channel.title}
            </h1>
            <p className="studio-sub" style={{ margin: "4px 0 0", fontSize: "0.95rem" }}>
              {channel.niche || "—"} · {channel.handle || "channel"} · {fmtNum(channel.subscribers)} subscribers
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="studio-ops-row" data-testid="public-kpis">
        <div className="studio-ops-card">
          <header><span className="studio-eyebrow"><BarChart3 size={12}/> In pipeline</span></header>
          <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{pipeline_total}</p>
        </div>
        <div className="studio-ops-card">
          <header><span className="studio-eyebrow"><Calendar size={12}/> Upcoming 30d</span></header>
          <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{upcoming.length}</p>
        </div>
        <div className="studio-ops-card">
          <header><span className="studio-eyebrow"><CheckCircle2 size={12}/> Pending approval</span></header>
          <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{pending_approvals.length}</p>
        </div>
        <div className="studio-ops-card">
          <header><span className="studio-eyebrow"><Users size={12}/> Team</span></header>
          <p style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{channel.team_size}</p>
        </div>
      </div>

      {/* Approval Inbox (auth-gated section) */}
      <div className="studio-section-h" style={{ marginTop: 12 }}>
        <h2><Inbox size={16}/> Approval inbox</h2>
      </div>
      {!signedInAs ? (
        <div className="studio-card" style={{ padding: 18 }} data-testid="inbox-signin">
          <p style={{ margin: "0 0 12px" }}>
            <Mail size={14} style={{ verticalAlign: "-2px", marginRight: 6 }}/>
            Approve or request changes — sign in with your email to view items pending your approval.
          </p>
          {linkSent ? (
            <p className="studio-muted" data-testid="inbox-link-sent">
              <CheckCircle2 size={14} style={{ verticalAlign: "-2px", marginRight: 6, color: "#16a34a" }}/>
              Link sent. Check <strong>{emailInput}</strong> — expires in 20 minutes.
            </p>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="email"
                className="studio-input"
                placeholder="you@client.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                style={{ flex: 1, minWidth: 220 }}
                data-testid="inbox-email-input"
              />
              <button
                className="studio-btn-primary"
                onClick={requestLink}
                disabled={linkBusy}
                data-testid="inbox-request-link"
              >
                {linkBusy ? <Loader2 size={14} className="ac-spin"/> : <Mail size={14}/>} Email me a sign-in link
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <p className="studio-muted" data-testid="inbox-signed-in">
            Signed in as <strong>{signedInAs}</strong>
          </p>
          {inboxLoading && (
            <div className="studio-card studio-loading"><Loader2 size={22} className="ac-spin"/><p>Loading inbox…</p></div>
          )}
          {!inboxLoading && inbox && (
            inbox.items.length === 0 ? (
              <div className="studio-card" style={{ padding: 18, textAlign: "center" }} data-testid="inbox-empty">
                <CheckCircle2 size={32} style={{ color: "#16a34a" }}/>
                <p>Nothing waiting for your approval right now. 🎯</p>
              </div>
            ) : (
              <div className="studio-grid" data-testid="inbox-list">
                {inbox.items.map((it) => (
                  <div key={it._id} className="studio-channel-card" data-testid={`inbox-item-${it._id}`}>
                    <div className="studio-channel-head">
                      <div className="studio-channel-meta" style={{ flex: 1 }}>
                        <h3 style={{ fontSize: "1rem" }}>{it.title}</h3>
                        <p>{STAGE_LABELS[it.stage] || it.stage}{it.publish_date ? ` · ${fmtDate(it.publish_date)}` : ""}</p>
                      </div>
                      {it.client_decision && (
                        <span className={`studio-role studio-role--${it.client_decision.decision === "approve" ? "designer" : "creator"}`}>
                          {it.client_decision.decision === "approve" ? "Approved" : "Changes asked"}
                        </span>
                      )}
                    </div>
                    {it.script_preview && (
                      <div style={{ padding: "0 16px 12px", color: "var(--st-text-muted)", fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-line" }}>
                        {it.script_preview}…
                      </div>
                    )}
                    <div style={{ padding: "0 16px 12px" }}>
                      <textarea
                        className="studio-input"
                        placeholder="Optional note for the team…"
                        rows={2}
                        value={noteFor[it._id] || ""}
                        onChange={(e) => setNoteFor((s) => ({ ...s, [it._id]: e.target.value }))}
                        data-testid={`inbox-note-${it._id}`}
                        style={{ width: "100%", resize: "vertical" }}
                      />
                    </div>
                    <div className="studio-channel-foot" style={{ gap: 8 }}>
                      <button
                        className="studio-btn-ghost"
                        onClick={() => decide(it._id, "changes")}
                        disabled={decidingId === it._id}
                        data-testid={`inbox-changes-${it._id}`}
                      >
                        <MessageSquare size={14}/> Request changes
                      </button>
                      <button
                        className="studio-btn-primary"
                        onClick={() => decide(it._id, "approve")}
                        disabled={decidingId === it._id}
                        data-testid={`inbox-approve-${it._id}`}
                      >
                        {decidingId === it._id ? <Loader2 size={14} className="ac-spin"/> : <Check size={14}/>} Approve
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}

      {/* Upcoming calendar */}
      <div className="studio-section-h"><h2>Upcoming · next 30 days</h2></div>
      {upcoming.length === 0 ? (
        <p className="studio-muted">No upcoming videos scheduled.</p>
      ) : (
        <div className="studio-grid" data-testid="public-upcoming">
          {upcoming.slice(0, 12).map((u) => (
            <div key={u._id} className="studio-channel-card" data-testid={`upcoming-${u._id}`}>
              <div className="studio-channel-head">
                <div className="studio-channel-meta" style={{ flex: 1 }}>
                  <h3 style={{ fontSize: "0.98rem" }}>{u.title}</h3>
                  <p>{fmtDate(u.publish_date)}</p>
                </div>
                <span className={`studio-role studio-role--${u.format === "short" ? "designer" : "creator"}`}>
                  {STAGE_LABELS[u.stage] || u.stage}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Top videos */}
      <div className="studio-section-h" style={{ marginTop: 32 }}><h2><TrendingUp size={16}/> Top performing</h2></div>
      {top_videos.length === 0 ? (
        <p className="studio-muted">No published videos yet.</p>
      ) : (
        <div className="studio-grid" data-testid="public-top">
          {top_videos.map((v) => (
            <div key={v._id} className="studio-channel-card" data-testid={`top-${v._id}`}>
              <div className="studio-channel-head">
                <div className="studio-channel-meta" style={{ flex: 1 }}>
                  <h3 style={{ fontSize: "0.98rem" }}>{v.title}</h3>
                  <p>{fmtDate(v.publish_date)}</p>
                </div>
              </div>
              <div className="studio-channel-stats">
                <div className="studio-stat">
                  <span className="studio-stat-icon"><BarChart3 size={14}/></span>
                  <div><strong>{fmtNum(v.views)}</strong><small>Views</small></div>
                </div>
                {v.ctr != null && (
                  <div className="studio-stat">
                    <span className="studio-stat-icon"><TrendingUp size={14}/></span>
                    <div><strong>{(v.ctr * 100).toFixed(1)}%</strong><small>CTR</small></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent publishes */}
      {recent_publishes.length > 0 && (
        <>
          <div className="studio-section-h" style={{ marginTop: 32 }}><h2>Recently published</h2></div>
          <ul className="studio-ops-list" data-testid="public-recent">
            {recent_publishes.slice(0, 10).map((r) => (
              <li key={r._id} className="studio-ops-li" data-testid={`recent-${r._id}`}>
                <strong>{r.title}</strong>
                <span>{fmtDate(r.publish_date)} · {fmtNum(r.views)} views</span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div style={{ marginTop: 60, padding: 18, textAlign: "center", color: "var(--st-text-muted)", fontSize: 12, borderTop: "1px solid var(--st-line)" }}>
        This is a read-only client view · Powered by <strong style={{ color: "var(--st-purple)" }}>MarkeeMix Studio</strong>
      </div>
    </div>
  );
}
